<script setup>

import SubmitButtonLargeComponent from '@buttons/SubmitButtonLargeComponent.vue';
import ButtonComponent from '@buttons/ButtonComponent.vue';
import ProgressBarComponent from '@formFields/ProgressBarComponent.vue';
import {inject, onMounted, ref, watch} from 'vue';
import {showErrorToast, showSuccessToast} from '@utils/Toast';
import {debounce} from 'lodash';
import useFileHandler from '@utils/useFileHandler';
import useFileDeleteHandler from '@utils/useFileDeleteHandler';
import ThumbnailComponentDelete from '@images/ThumbnailComponentDelete.vue';
import NoUploadedProfilePhotoFoundComponent from '@utils/NoUploadedProfilePhotoFoundComponent.vue';
import MainProgressBarComponent from '../../Helper/MainProgressBarComponent.vue';
import {handleErrorResponse} from '@utils/errorHandler';
import {isValidName} from '@utils/isValidName';
import CountrySelectBoxComponent from '@formFields/CountrySelectBoxComponent.vue';

const props = defineProps({
    csrf: String,
    route: String,
    routeSuccess: String,
    routeCollectCountries: String,
    user: Object,
});

// Inject Axios
const axios = inject('axios');

const title = 'Please provide your personal info.';

const LABEL_FIRST_NAME = 'First Name';
const LABEL_LAST_NAME = 'Last Name';
const LABEL_COUNTRY = 'Select Country';

const isFormValid = ref(false);
const first_name = ref(props.user.first_name || '');
const last_name = ref(props.user.last_name || '');
const country_id = ref(props.user.country_id || null);

const countries = ref([]);

const errors = ref({
    first_name: null,
    last_name: null,
    country_id: null,
});

const delayedRedirection = debounce(() => {
    window.location.href = props.routeSuccess;
}, 2000);

const validateForm = () => {
    // Check the validity of the input field
    const isFirstNameValid = first_name.value !== '' && isValidName(first_name.value);
    const isLastNameValid = last_name.value !== '' && isValidName(last_name.value);

    errors.value.first_name = !isFirstNameValid ? LABEL_FIRST_NAME +  ' is required' :  null;
    errors.value.last_name = !isLastNameValid ? LABEL_LAST_NAME +  ' is required' :  null;
    errors.value.country_id = country_id.value === null ? LABEL_COUNTRY +  ' is required' :  null;

    isFormValid.value = isFirstNameValid && isLastNameValid && country_id.value.value !== null;
};

const fetchCountries = async () => {
    try {
        //Send a POST request to the server
        const response = await axios.get(props.routeCollectCountries, {
            headers: {
                'X-CSRF-TOKEN': props.csrf // Include CSRF token here
            }
        });
        //Handle response here
        if(response.status === 201) {
            const data = response.data;
            if(data.success === 1) {
                return data.countries;
            } else {
                return [];
            }
        } else {
            if(response.message) {
                showErrorToast(response.message);
            }
        }

    } catch (error) {
        showErrorToast(handleErrorResponse(error));
        console.error('Submission error', error);
    }
};

const submitForm = async () => {

    validateForm();
    if(!isFormValid.value) {
        // showErrorToast('Invalid form values');
        return false;
    }
    //Prepare the data to be sent
    const formData = {
        first_name: first_name.value,
        last_name: last_name.value,
        country_id: country_id.value,
    };

    try {
        //Send a POST request to the server
        const response = await axios.post(props.route, formData, {
            headers: {
                'X-CSRF-TOKEN': props.csrf // Include CSRF token here
            }
        });
        //Handle response here
        if(response.status === 200) {
            const data = response.data;
            if(data.success === 1) {
                showSuccessToast(data.message);
                delayedRedirection();
            } else {
                showErrorToast(data.message);
            }
        } else {
            if(response.message) {
                showErrorToast(response.message);
            }
        }

    } catch (error) {
        showErrorToast(handleErrorResponse(error));
        console.error('Submission error', error);
    }
};

onMounted(async () => {
    countries.value = await fetchCountries();
    validateForm()
});

</script>

<template>
    <div class="container sm:max-w-6xl text-center py-6">
        <main-progress-bar-component :upload-percentage="100/1.5"></main-progress-bar-component>
        <div class="font-bold text-xl md:text-2xl pb-6 pt-12 md:pt-20 leading-tight" v-html="title"></div>

        <form :action="route" id="frmClientLogin" method="post"
              @submit.prevent="submitForm"
        >
            <div class="text-center container max-w-100 md:w-100 bg-white rounded-xl mb-8">
                <div class="wrap-form text-left">
                    <div class="form-row">
                        <input type="text" v-model="first_name" class="form-control" autocomplete="off"
                               :placeholder="LABEL_FIRST_NAME">
                        <div v-if="errors.first_name" class="text-sm text-red">{{ errors.first_name }}</div>
                    </div>

                    <div class="form-row">
                        <input type="text" v-model="last_name" class="form-control" autocomplete="off"
                               :placeholder="LABEL_LAST_NAME">
                        <div v-if="errors.last_name" class="text-sm text-red">{{ errors.last_name }}</div>
                    </div>

                    <div class="form-row">
                        <country-select-box-component
                            :countries="countries"
                            v-model="country_id"
                            :country_id="country_id"
                            :placeholder="LABEL_COUNTRY"
                        ></country-select-box-component>
                        <div v-if="errors.country_id" class="text-sm text-red">{{ errors.country_id }}</div>
                    </div>

                </div>
            </div>
            <div class="flex items-center justify-center gap-4 container max-w-100 md:w-100">
                <div class="flex-1" v-if="isFormValid">
                    <ButtonComponent
                        title="Skip it"
                        :href="routeSuccess"
                        btn-class="btn-red-outline w-full text-xl !py-4"
                    ></ButtonComponent>
                </div>
                <div class="flex-1">
                    <SubmitButtonLargeComponent
                        title="Continue"
                        btn-class="btn-green-5 w-full !py-4"
                    >
                    </SubmitButtonLargeComponent>
                </div>
            </div>
        </form>
    </div>
</template>

<style scoped lang="scss">
.form {
    &-row {
        @apply mb-6;
    }
    &-control {
        @apply bg-white py-3;
    }
}
</style>
